import { useRoute } from '@react-navigation/native';
import { Meta, Spinner, theme } from 'aimo-ui';
import InfoBox from 'components/common/InfoBox';
import { InfoIcon } from 'components/icons/InfoIcon';
import { t } from 'i18next';
import React from 'react';
import { Subscription } from 'stores/subscriptionStore';
import styled from 'styled-components/native';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { BillingType, Maybe } from 'types/generatedSchemaTypes';
import { salesOrderExpiration } from 'utils/permitUtils';
import { SalesOrderReceipt } from './SalesOrderReceiptClass';

const ActivatingContainer = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 1;
`;

const InfoMessageContainer = styled.View`
  flex-direction: row;
  align-items: center;
  margin-left: 8px;
  margin-right: 8px;
  padding-right: 20px;
`;

const InfoTextContainer = styled.View`
  flex: 1;
  margin: 8px;
`;

type InfoBoxProps = {
  salesOrderReceipt: Maybe<SalesOrderReceipt>;
  loading?: boolean;
  bgColor?: string;
};

type ActivateInfoBoxProps = {
  salesOrderReceipt: Maybe<SalesOrderReceipt | Subscription>;
  bgColor?: string;
};

const PermitInfoBox: React.FC<{
  children: React.ReactNode;
  showInfoIcon: boolean;
  bgColor?: string;
}> = ({ children, showInfoIcon, bgColor = theme.colors.lightBg.yellow }) => (
  <ColumnWithMargins mb={14}>
    <InfoBox
      bgColor={bgColor}
      padding={10}
      radius={20}
      leftComponent={showInfoIcon && <InfoIcon />}
      margin={0}>
      {children}
    </InfoBox>
  </ColumnWithMargins>
);

export const PaymentErrorInfoBox: React.FC<InfoBoxProps> = ({
  salesOrderReceipt,
  loading = false,
  bgColor = theme.colors.lightBg.yellow,
}) => {
  const route = useRoute();

  if (!salesOrderReceipt || route.name === 'permits' || loading) {
    return null;
  }

  const {
    isWaiting,
    isFailedPayment,
    isActivating,
    isFailedRecurringPaymentForSalesOrder,
  } = salesOrderReceipt;

  const showErrorMessage =
    (isFailedPayment && !isActivating) || isFailedRecurringPaymentForSalesOrder;

  return (
    <>
      {showErrorMessage && (
        <PermitInfoBox showInfoIcon={isWaiting} bgColor={bgColor}>
          <InfoMessageContainer>
            <InfoTextContainer>
              <Meta>
                {t('permit.paymentFailed')}. {t('permit.resolvePaymentIntro')}
              </Meta>
            </InfoTextContainer>
          </InfoMessageContainer>
        </PermitInfoBox>
      )}
    </>
  );
};

export const ActivateInfoBox: React.FC<ActivateInfoBoxProps> = ({
  salesOrderReceipt,
  bgColor = theme.colors.lightBg.yellow,
}) => {
  if (!salesOrderReceipt) {
    return null;
  }

  // Handle both SalesOrderReceipt and Subscription types
  const isSalesOrderReceipt = salesOrderReceipt instanceof SalesOrderReceipt;

  const isWaiting = isSalesOrderReceipt
    ? salesOrderReceipt.isWaiting
    : (salesOrderReceipt as Subscription).status === 'WAITING_FOR_ACTIVATION' ||
      (salesOrderReceipt as Subscription).status === 'WAITING_FOR_PAYMENT';

  const isActivating = isSalesOrderReceipt
    ? salesOrderReceipt.isActivating
    : (salesOrderReceipt as Subscription).status === 'ACTIVATING';

  const isUpdating = isSalesOrderReceipt
    ? salesOrderReceipt.isUpdating
    : (salesOrderReceipt as Subscription).status === 'UPDATING';

  const orderStatus = isSalesOrderReceipt
    ? salesOrderReceipt.orderStatus
    : (salesOrderReceipt as Subscription).status;

  // For SalesOrderReceipt, check expiration; for Subscription, default to not expired
  const { isExpired, expirationDateString } = isSalesOrderReceipt
    ? salesOrderExpiration(salesOrderReceipt)
    : { isExpired: false, expirationDateString: '' };

  return (
    <PermitInfoBox showInfoIcon={isWaiting} bgColor={bgColor}>
      <ActivatingContainer>
        <Meta>
          {t(
            `permit.${
              isExpired && isWaiting ? 'expired' : `orderStatus.${orderStatus}`
            }`,
            {
              date: expirationDateString,
            }
          )}
        </Meta>
        {(isActivating || isUpdating) && <Spinner size={24} />}
      </ActivatingContainer>
    </PermitInfoBox>
  );
};

export const PermitBillingTypeInfoBox: React.FC<{
  billingType: BillingType | undefined;
}> = ({ billingType }) => {
  if (!billingType) {
    return null;
  }

  return (
    <>
      {billingType === 'CALENDAR_FULL' && (
        <PermitInfoBox showInfoIcon={false}>
          <InfoMessageContainer>
            <InfoTextContainer>
              <Meta>{t('permit.billingTypeCalendarFullInfo')}</Meta>
            </InfoTextContainer>
          </InfoMessageContainer>
        </PermitInfoBox>
      )}
    </>
  );
};
